/* investeeng-base.js — v4 platform runtime.
   - GA4 wrapper
   - QR scan + Amazon click + simulator engagement tracking
   - Provider-agnostic subscribe handler (Klaviyo / Mailchimp / Shopify)
   - Intersection Observer entrance animations
   - Sticky header scroll-state, mobile nav toggle
   - Reading-progress bar on articles
   - CountUp.js wrapper for animated stats
   - Chart.js dark-theme defaults (yellow accent on near-black)
   - Brand tokens exposed as window.IT_TOKENS for simulator scripts
*/
(function () {
  'use strict';

  // ---------- Chart.js — KDP forest + gold defaults ----------
  function applyChartDefaults() {
    if (!window.Chart) return setTimeout(applyChartDefaults, 50);
    Chart.defaults.color = 'rgba(244, 235, 211, 0.62)';
    Chart.defaults.borderColor = 'rgba(244, 235, 211, 0.12)';
    Chart.defaults.font.family = "'Helvetica Neue', Helvetica, Arial, sans-serif";
    Chart.defaults.scale.grid.color = 'rgba(244, 235, 211, 0.10)';
    Chart.defaults.scale.ticks.color = 'rgba(244, 235, 211, 0.62)';
    Chart.defaults.plugins.legend.labels.font = { family: 'Helvetica Neue, Helvetica, Arial, sans-serif', size: 11, weight: '700' };
    Chart.defaults.plugins.legend.labels.color = 'rgba(244, 235, 211, 0.78)';
    Chart.defaults.plugins.tooltip.backgroundColor = '#0A3527';
    Chart.defaults.plugins.tooltip.titleColor = '#F4EBD3';
    Chart.defaults.plugins.tooltip.bodyColor = '#F4EBD3';
    Chart.defaults.plugins.tooltip.borderColor = 'rgba(240, 196, 25, 0.55)';
    Chart.defaults.plugins.tooltip.borderWidth = 1;
  }
  applyChartDefaults();

  // ---------- Brand tokens for simulator scripts ----------
  window.IT_TOKENS = {
    bg:          '#0F4D3A',
    bgDeep:      '#0A3527',
    bgCard:      '#0D4232',
    bgRaised:    '#114F3D',
    gold:        '#F0C419',
    goldHover:   '#FFD333',
    goldDim:     'rgba(240, 196, 25, 0.18)',
    goldGlow:    'rgba(240, 196, 25, 0.28)',
    /* yellow* aliases for simulators that reference the previous token names */
    yellow:      '#F0C419',
    yellowHover: '#FFD333',
    yellowDim:   'rgba(240, 196, 25, 0.18)',
    yellowGlow:  'rgba(240, 196, 25, 0.28)',
    textPrimary:   '#F4EBD3',
    textSecondary: 'rgba(244, 235, 211, 0.78)',
    textMuted:     'rgba(244, 235, 211, 0.56)',
    border:        'rgba(244, 235, 211, 0.14)',
    green:       '#4ADE80',
    red:         '#F87171',
    blue:        '#93C5FD',
    purple:      '#C4B5FD',
    orange:      '#FDBA74',
    /* legacy aliases (preserve older simulator JS that still references coral/cream/obsidian) */
    coral:       '#F0C419',
    coralDeep:   '#FFD333',
    coralFill:   'rgba(240, 196, 25, 0.18)',
    cream:       '#F4EBD3',
    creamMid:    'rgba(244, 235, 211, 0.78)',
    creamLow:    'rgba(244, 235, 211, 0.56)',
    obsidian:    '#0F4D3A',
    obsidian2:   '#0D4232',
    obsidian3:   '#114F3D',
    hairline:    'rgba(244, 235, 211, 0.14)',
    forestLight: '#86EFAC',
    amberLight:  '#FCD34D',
    plumLight:   '#DDD6FE',
    crimsonLight:'#FECACA'
  };

  // ---------- GA4 helper ----------
  window.IT = window.IT || {};
  IT.track = function (event, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', event, params || {});
      }
    } catch (e) { /* analytics never breaks the page */ }
  };

  // ---------- Format helpers ----------
  IT.fmt = {
    money: function (n, opts) {
      opts = opts || {};
      var abs = Math.abs(n);
      if (opts.compact && abs >= 1e6)  return '$' + (n / 1e6).toFixed(2) + 'M';
      if (opts.compact && abs >= 1000) return '$' + (n / 1000).toFixed(1) + 'k';
      return '$' + Math.round(n).toLocaleString('en-US');
    },
    pct: function (n) { return (n * 100).toFixed(1) + '%'; },
    yrs: function (n) { return n + (n === 1 ? ' year' : ' years'); }
  };
  IT.url = {
    read: function () { return Object.fromEntries(new URLSearchParams(window.location.search)); },
    write: function (params) {
      var q = new URLSearchParams();
      Object.keys(params).forEach(function (k) {
        var v = params[k];
        if (v !== null && v !== undefined && v !== '') q.set(k, v);
      });
      var qs = q.toString();
      var url = window.location.pathname + (qs ? '?' + qs : '');
      window.history.replaceState(null, '', url);
    }
  };

  // ---------- Intersection Observer entrance animations ----------
  function setupAnimations() {
    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('.animate-in').forEach(el => el.classList.add('visible'));
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.animate-in').forEach(el => observer.observe(el));
  }

  // ---------- CountUp wrapper ----------
  function setupCountUp() {
    if (typeof IntersectionObserver === 'undefined' || typeof window.countUp === 'undefined') {
      // Fallback: just set the final value
      document.querySelectorAll('[data-countup]').forEach(el => {
        el.textContent = el.dataset.countup;
      });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        var raw = el.dataset.countup;
        var hasPlus = raw.indexOf('+') !== -1;
        var num = parseFloat(raw.replace(/[+,]/g, ''));
        if (isNaN(num)) { el.textContent = raw; observer.unobserve(el); return; }
        var counter = new window.countUp.CountUp(el, num, {
          duration: 1.8,
          useEasing: true,
          separator: ',',
          suffix: hasPlus ? '+' : ''
        });
        if (!counter.error) counter.start();
        else el.textContent = raw;
        observer.unobserve(el);
      });
    }, { threshold: 0.4 });
    document.querySelectorAll('[data-countup]').forEach(el => observer.observe(el));
  }

  // ---------- Header scroll state ----------
  function setupHeaderScroll() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    function onScroll() {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---------- Mobile nav toggle ----------
  function setupNavToggle() {
    var btn = document.querySelector('[data-nav-toggle]');
    var nav = document.querySelector('[data-site-nav]');
    if (!btn || !nav) return;
    btn.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    });
  }

  // ---------- Reading progress bar ----------
  function setupReadingProgress() {
    var article = document.querySelector('[data-reading-progress]');
    if (!article) return;
    var bar = document.createElement('div');
    bar.className = 'reading-progress';
    document.body.appendChild(bar);
    function update() {
      var top = article.offsetTop;
      var height = article.scrollHeight;
      var scrolled = window.scrollY - top + window.innerHeight;
      var pct = Math.max(0, Math.min(1, scrolled / height));
      bar.style.width = (pct * 100) + '%';
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // ---------- Simulator filter (hub) ----------
  function setupSimFilter() {
    var filterRoot = document.querySelector('[data-sim-filter]');
    if (!filterRoot) return;
    var btns = filterRoot.querySelectorAll('.sim-filter-btn');
    var search = document.querySelector('[data-sim-search]');
    var cards = document.querySelectorAll('[data-sim-category]');
    var activeCategory = 'all';
    var activeQuery = '';

    function apply() {
      cards.forEach(function (c) {
        var matchesCat = (activeCategory === 'all' || c.dataset.simCategory === activeCategory);
        var name = (c.dataset.simName || '').toLowerCase();
        var matchesSearch = !activeQuery || name.indexOf(activeQuery) !== -1;
        c.style.display = (matchesCat && matchesSearch) ? '' : 'none';
      });
    }
    btns.forEach(function (b) {
      b.addEventListener('click', function () {
        btns.forEach(function (x) { x.classList.remove('is-active'); });
        b.classList.add('is-active');
        activeCategory = b.dataset.filter;
        IT.track('filter_used', { page: 'simulators', category: activeCategory });
        apply();
      });
    });
    if (search) {
      search.addEventListener('input', function () {
        activeQuery = search.value.toLowerCase().trim();
        apply();
      });
    }
  }

  // ---------- Chapter accordion ----------
  function setupChapters() {
    document.querySelectorAll('[data-chapter]').forEach(function (ch) {
      var btn = ch.querySelector('.chapter__btn');
      if (!btn) return;
      btn.addEventListener('click', function () { ch.classList.toggle('is-open'); });
    });
  }

  // ---------- QR-scan detection ----------
  function detectQR() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('ref') === 'qr') {
      var sim = document.body.dataset.simulator || 'unknown';
      var chapter = document.body.dataset.chapter || null;
      IT.track('qr_scan', { simulator: sim, chapter: chapter });
    }
  }

  // ---------- Simulator open + engagement ----------
  function setupSimulatorTracking() {
    var sim = document.body.dataset.simulator;
    if (!sim) return;
    var params = new URLSearchParams(window.location.search);
    var source = params.get('ref') === 'qr' ? 'qr' : (params.get('source') || 'direct');
    IT.track('simulator_open', {
      name: sim,
      chapter: document.body.dataset.chapter || null,
      source: source
    });

    var root = document.querySelector('[data-engagement-root]');
    if (!root) return;
    var lastFire = 0;
    var totalActiveMs = 0;
    var lastActivity = 0;
    function note() {
      var now = Date.now();
      if (lastActivity && (now - lastActivity) < 30000) totalActiveMs += (now - lastActivity);
      lastActivity = now;
      if (totalActiveMs >= 30000 && (now - lastFire) > 30000) {
        IT.track('simulator_engaged', { name: sim, duration_sec: Math.round(totalActiveMs / 1000) });
        lastFire = now;
      }
    }
    ['input', 'change', 'click'].forEach(function (e) {
      root.addEventListener(e, note, { passive: true });
    });
  }

  // ---------- Amazon click tracking ----------
  function setupAmazonTracking() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[data-amazon]');
      if (!a) return;
      IT.track('book_click', {
        book: a.dataset.book || 'investeeng',
        format: a.dataset.format || 'print',
        source: a.dataset.source || 'unknown'
      });
    });
  }

  // ---------- Share-link copy ----------
  function setupShareLinks() {
    document.querySelectorAll('[data-share-link]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = window.location.href;
        navigator.clipboard.writeText(url).then(function () {
          var orig = btn.textContent;
          btn.textContent = '✓ Copied';
          setTimeout(function () { btn.textContent = orig; }, 1800);
        });
      });
    });
  }

  // ---------- Subscribe form (provider-agnostic) ----------
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var GENERIC_ERR = 'Something went wrong. Try again in a moment.';
  function showFormError(form, msg) {
    var errBox = form.querySelector('.subscribe-error');
    if (!errBox) return;
    errBox.textContent = msg;
    errBox.hidden = false;
  }

  function setupSubscribeForms() {
    document.querySelectorAll('.subscribe-form').forEach(function (form) {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();
        var emailInput = form.querySelector('input[type="email"]');
        var email = emailInput ? emailInput.value.trim() : '';
        var success = form.querySelector('.subscribe-success');
        var errBox  = form.querySelector('.subscribe-error');
        var inputWrap = form.querySelector('.subscribe-input-wrap');
        if (errBox)  errBox.hidden = true;
        if (success) success.hidden = true;

        if (!email || !EMAIL_RE.test(email)) {
          showFormError(form, 'Please enter a valid email address.');
          if (emailInput) emailInput.focus();
          return;
        }

        var btn = form.querySelector('button[type="submit"]');
        var origText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Subscribing…';

        var cfg = window.INVESTEENG_CONFIG || {};
        var provider = cfg.emailProvider || 'klaviyo';
        var sourceField = form.querySelector('[name="form_source"]');
        var source = (sourceField && sourceField.value) || 'unknown';

        try {
          if (provider === 'klaviyo' && cfg.klaviyoCompanyId && cfg.klaviyoListId) {
            var res = await fetch(
              'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId),
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'revision': '2024-02-15'
                },
                body: JSON.stringify({
                  data: {
                    type: 'subscription',
                    attributes: {
                      profile: { data: { type: 'profile', attributes: { email: email } } },
                      custom_source: source
                    },
                    relationships: { list: { data: { type: 'list', id: cfg.klaviyoListId } } }
                  }
                })
              }
            );
            if (!res.ok && res.status !== 202) throw new Error('klaviyo:' + res.status);
          } else if (provider === 'mailchimp' && cfg.mailchimpUrl) {
            await new Promise(function (resolve, reject) {
              var cb = 'mc_jsonp_' + Date.now();
              var s = document.createElement('script');
              window[cb] = function (data) {
                delete window[cb]; document.body.removeChild(s);
                if (data && data.result === 'success') resolve();
                else reject(new Error(data && data.msg || 'mailchimp error'));
              };
              var url = cfg.mailchimpUrl
                .replace('/post?', '/post-json?')
                + '&EMAIL=' + encodeURIComponent(email)
                + '&c=' + cb;
              s.src = url;
              document.body.appendChild(s);
              setTimeout(function () { reject(new Error('mailchimp timeout')); }, 8000);
            });
          } else if (provider === 'shopify') {
            // {% form 'customer' %} provides the action URL + authenticity_token.
            // Shopify accepts the POST and creates the customer regardless of
            // whether it responds with 302 / 200 / captcha challenge — checking
            // res.ok is unreliable across Safari + Shopify's bot protection,
            // so trust any non-network-error fetch.
            var action = form.action || cfg.shopifyContactUrl || '/contact';
            await fetch(action, {
              method: 'POST',
              body: new FormData(form),
              credentials: 'same-origin',
              redirect: 'manual'
            });
          } else {
            // No provider configured — still surface the success UI for preview/dev
            await new Promise(r => setTimeout(r, 600));
          }

          if (inputWrap) inputWrap.hidden = true;
          if (success)   success.hidden = false;
          IT.track('subscribe', { method: provider, source: source });
        } catch (err) {
          showFormError(form, GENERIC_ERR);
          btn.disabled = false;
          btn.textContent = origText;
          IT.track('subscribe_error', { source: source, error: 'api' });
        }
      });
    });
  }

  // ---------- Mission scroll depth ----------
  function setupMissionScroll() {
    if (!document.body.classList.contains('template-page-mission')) return;
    var fired = {};
    function check() {
      var pct = Math.round((window.scrollY + window.innerHeight) / document.body.scrollHeight * 100);
      [50, 75, 95].forEach(function (b) {
        if (pct >= b && !fired[b]) {
          fired[b] = true;
          IT.track('mission_read', { scroll_depth_pct: b });
        }
      });
    }
    window.addEventListener('scroll', check, { passive: true });
  }

  // ---------- Bootstrap ----------
  document.addEventListener('DOMContentLoaded', function () {
    setupAnimations();
    setupCountUp();
    setupHeaderScroll();
    setupNavToggle();
    setupReadingProgress();
    setupSimFilter();
    setupChapters();
    detectQR();
    setupSimulatorTracking();
    setupAmazonTracking();
    setupShareLinks();
    setupSubscribeForms();
    setupMissionScroll();
  });

})();
